import Image from '@/components/image';
import NextLink from '@/components/next-link';
import CartRules from '@/components/product/cart-rules';
import { useRouter } from '@/i18n/navigation';
import useItemListImpression from '@/hooks/useItemListImpression';
import useSelectItem from '@/hooks/useSelectItem';
import useCart from '@/store/cart.store';
import { validatePrice } from '@/utils/category';
import { setCookie } from 'cookies-next';
import { memo } from 'react';
import AddToCart from '../buttons/add-to-cart';
import AddToWishlist from '../buttons/add-to-wishlist';
import QtyControl from '../buttons/qty-control';
import PricingCard from './pricing-card';
import ProductLabels from './product-labels';

const ProductCard = ({ product, priority = false, page, index = 0 }) => {
  const listRef = useItemListImpression(product, index);
  const selectItem = useSelectItem(product, index);
  const is_stock = product?.stock?.is_in_stock && product?.stock?.qty > 0;
  const { cartItems } = useCart();
  const isItemInCart = cartItems?.find(
    (item) => item?.extension_attributes?.product_sku === product?.sku
  );
  const productName = Array.isArray(product?.name)
    ? product?.name?.[0]
    : product?.name;
  const { push } = useRouter();

  const prices = validatePrice(product?.prices_with_tax);

  const brandUrl = product?._category_?.sub_categories?.at(-1)?.url_key;

  return (
    <div
      ref={listRef}
      className='relative flex flex-col gap-2.25 overflow-hidden rounded-xl border border-gray-100 p-3 md:gap-1.75'
    >
      <div className='relative mx-auto flex w-full items-center justify-center max-lg:max-h-54.5 max-lg:min-h-54.5 max-md:max-h-45 max-md:min-h-45 max-sm:mb-4 max-sm:max-h-36 max-sm:min-h-36'>
        <div
          className={`flex w-full items-center justify-center ${'max-h-[182px] min-h-[182px] max-[570px]:h-full'} z-10 cursor-pointer overflow-hidden`}
        >
          <Image
            src={
              product?._media_?.image?.at(0)?.image ??
              product?.image_url ??
              product?.magento_product_ur
            }
            className='w-auto cursor-pointer object-contain max-lg:max-h-41.75 max-lg:min-h-41.75 max-md:h-auto max-md:max-h-32 max-sm:max-h-27 lg:size-[168px]'
            alt={productName}
            width={160}
            onClick={() => {
              selectItem();
              push(`/${product?.innova_url}`);
              setCookie('product_view_type', page);
            }}
            height={160}
            // withShimmer={!priority}
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            quality={85}
          />
        </div>
        <AddToWishlist
          sku={product?.sku}
          product={product}
          iconSize='!size-5'
          fromProductCard
          page={page}
          index={index}
        />

        <ProductLabels
          {...{
            showOfferLabel: is_stock,
            label: product?.labels,
            prices,
            requirePrescription:
              product?.prescription_upload_required &&
              product?.prescription_upload_required !== 'No',
            flashSaleStartDate: product?.flash_sale_start_date,
            flashSaleEndDate: product?.flash_sale_end_date,
          }}
        />
      </div>

      <div className='flex h-full flex-col justify-end gap-2 sm:gap-[9px] md:gap-2.5 lg:gap-[11px]'>
        <div className='h-6'>
          <CartRules
            rules={
              product?.cart_rule?.length
                ? product?.cart_rule
                : product?.type_id === 'bundle' &&
                    product?.option_text_offer_type
                  ? [
                      {
                        rule_id: Array.isArray(product?.offer_type)
                          ? product?.offer_type?.at(0)
                          : product?.offer_type,
                        labels: [
                          ,
                          product?.option_text_offer_type || '',
                          product?.option_text_offer_type || '',
                        ],
                        name: 'Offers',
                      },
                    ]
                  : []
            }
            showOfferLabel={is_stock}
            showBulkDiscount={product?.tier_prices?.length > 0}
          />
        </div>
        <div className='flex items-center justify-between'>
          <NextLink
            href={`/b/${product?.brand_url ?? brandUrl ?? '/'}`}
            className={`min-h-5 cursor-pointer text-sm text-gray-500 ${product?.brand_label ? '' : 'invisible'} `}
          >
            {product?.brand_label}
          </NextLink>
        </div>

        <NextLink
          className='text-dark-300 line-clamp-2 h-10 items-start text-base leading-[19px] font-bold max-sm:text-sm sm:h-11.5 sm:leading-[21px] md:h-12.5 md:leading-[22.5px] lg:h-12.5 lg:leading-[25px]'
          href={`/${product?.innova_url}`}
          onClick={() => {
            selectItem();
            setCookie('product_view_type', page);
          }}
        >
          {productName}
        </NextLink>

        <PricingCard prices={prices} showOfferLabel={is_stock} />
        {isItemInCart ? (
          <QtyControl page={page} product={product} item={isItemInCart} />
        ) : (
          <AddToCart product={product} page={page} />
        )}
      </div>
    </div>
  );
};

export default memo(ProductCard);
